import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import * as Api from '../../../weborder/Api';
import { Typography } from '../../atoms';
import {
  Clock as ClockIcon,
  OrderCollection as CollectionIcon,
  OrderDelivery as DeliveryIcon,
  Profile as ProfileIcon,
  Receipt as ReceiptIcon,
} from '../../icons';
import { IconLineItem } from '../../molecules/IconLineItem';

const useStyles = makeStyles((theme: Theme) => ({
  orderDescription: {
    margin: 0,
    padding: theme.spacing(0, 2, 1),
  },
  [theme.breakpoints.up('sm')]: {
    orderDescription: {
      padding: theme.spacing(0, 3, 1),
    },
  },
  [theme.breakpoints.up('md')]: {
    orderDescription: {
      padding: theme.spacing(0, 0, 1),
    },
  },
}));

export type OrderDescriptionListProps = {
  address: string;
  customerName?: string;
  deliveryType: Api.DeliveryType;
  hasOrderTime?: boolean;
  isError?: boolean;
  isCancelled?: boolean;
  orderId: number;
  restaurantName: string;
  timeOfOrder: string;
  translate: any;
};

export function OrderDescriptionList(
  props: OrderDescriptionListProps,
): JSX.Element {
  const {
    address,
    customerName = null,
    deliveryType,
    hasOrderTime = false,
    isCancelled = false,
    isError = false,
    orderId,
    restaurantName,
    timeOfOrder,
    translate,
  } = props;

  const classes = useStyles();

  const hasCustomerName = customerName !== null && customerName !== '';
  const isDelivery = deliveryType === Api.DeliveryType.Delivery;

  const orderIcon = isDelivery ? <DeliveryIcon /> : <CollectionIcon />;

  const orderTypeLabel = isDelivery
    ? translate('OrderCompletePage_DeliverTo')
    : translate('Collect_from');

  const getOrderTimeLabel = (): string => {
    if (isError || isCancelled) {
      return translate('OrderComplete_PlacedAt');
    }

    return isDelivery
      ? (translate('CheckoutPreorder_deliveryTime') as string)
      : (translate('OrderCompletePage_CollectionTime') as string);
  };

  return (
    <ul className={classes.orderDescription}>
      <IconLineItem
        content={
          <>
            <Typography>{address}</Typography>

            {isDelivery && (
              <Typography variant="body2-secondary">
                {translate('From')} {restaurantName}
              </Typography>
            )}
          </>
        }
        icon={orderIcon}
        label={orderTypeLabel as string}
      />

      {hasOrderTime && (
        <IconLineItem
          content={timeOfOrder}
          icon={<ClockIcon />}
          label={getOrderTimeLabel()}
        />
      )}

      {!isCancelled && !!orderId && (
        <IconLineItem
          content={`#${orderId}`}
          icon={<ReceiptIcon />}
          label={translate('Order_number') as string}
        />
      )}

      {hasCustomerName && (
        <IconLineItem
          content={customerName}
          icon={<ProfileIcon />}
          label={
            translate('OrderConfirmationPage_UserInfo_NameOnOrder') as string
          }
        />
      )}
    </ul>
  );
}
